import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { AdoptStudentModal } from '@/student-board/AdoptStudentModal';
import { AssignStudentModal } from '@/student-board/AssignStudentModal';
import { EncourageStudentModal } from '@/student-board/EncourageStudentModal';
import { PassOnStudentModal } from '@/student-board/PassOnStudentModal';
import { ActionsCell } from '@/tables/ActionsCell';
import { listFiltersToFilterFields } from '@/tables/listFilterFields';
import { ModeIconCell } from '@/tables/ModeIconCell';
import { useWbsTableQuery } from '@/tables/useWbsTableQuery';
import type { Column } from '@/tables/WbsTable';
import WbsTable from '@/tables/WbsTable';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import { AvatarImg, NameCell, PageContainer } from '~/styles/boardStyles';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import type { Student } from '~/types/studentTypes';
import { formatLongDate } from '~/utils/dateFormat';
import { apiLanguageToLocale } from '~/utils/languageFormat';

type StudentBoardItem = components['schemas']['StudentBoardItem'];

/** Identifies this board's filter set to `GET /v3/lists/{list_key}/filters`. */
const LIST_KEY = 'general-student-board';
const TABLE_KEY = 'student-board';

function boardItemToStudent(item: StudentBoardItem): Student {
  return {
    id: String(item.id),
    name: item.display_name,
    age: item.age,
    gender: item.gender,
    language: apiLanguageToLocale(item.language),
    city: item.city,
    state: item.region ?? undefined,
    country: item.country,
    intro: item.intro ?? undefined,
    introProgress: item.intro_progress ?? undefined,
    registered: item.registered,
    mode: item.profile_type,
    actions: item.actions,
    encouragementCount: item.encouragement_count,
    avatarUrl: item.avatar_url,
    needsEncouragement: item.needs_encouragement,
  };
}

/**
 * `outreach_group_id` and `campaign_id` are strings on the wire: the endpoint
 * takes `any` or a numeric id, where `any` means every congregation or campaign
 * of mine. So there's no `Number()` coercion, only empty-value normalisation.
 */
function toOutreachValue(value: unknown): string | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  return String(value);
}

/** Language is repeated once per selected code, so it is always a list. */
function toLanguageList(value: unknown): string[] | undefined {
  if (Array.isArray(value)) return value.length > 0 ? value : undefined;
  if (typeof value === 'string' && value !== '') return [value];
  return undefined;
}

export const Route = createFileRoute('/_app/student-board/')({
  component: StudentBoardScreen,
});

export default function StudentBoardScreen() {
  const { t } = useTranslation();
  const languageLabels = useLanguageLabels();
  const [adoptingStudentId, setAdoptingStudentId] = useState<number | null>(
    null,
  );
  const [passingOnStudentId, setPassingOnStudentId] = useState<number | null>(
    null,
  );
  const [encouragingStudentId, setEncouragingStudentId] = useState<
    number | null
  >(null);
  const [assigningStudentId, setAssigningStudentId] = useState<number | null>(
    null,
  );

  // The panel's field keys are the endpoint's own filter keys (the fields are
  // built from its metadata below), so this only normalises the value shapes
  // the filter controls can't produce themselves.
  const mapFilters = useCallback(
    (filters: Record<string, unknown>): Record<string, unknown> => ({
      ...filters,
      // `group_id` means the group tab's board and is gated to admins and mods,
      // so this screen must never send it. An older persisted filter may still
      // hold a `group_id` key, and passing it through would 403;
      // `isEmptyFilterValue` drops `undefined`.
      group_id: undefined,
      outreach_group_id: toOutreachValue(filters.outreach_group_id),
      campaign_id: toOutreachValue(filters.campaign_id),
      language: toLanguageList(filters.language),
    }),
    [],
  );

  const table = useWbsTableQuery({ tableKey: TABLE_KEY, mapFilters });

  const { data: filtersData } = api.getListFilters.useQuery({
    path: { list_key: LIST_KEY },
  });
  const {
    data: boardData,
    isFetching,
    refetch,
  } = api.getStudentBoard.useQuery({ query: table.params });

  const students = useMemo(
    () => (boardData?.data ?? []).map(boardItemToStudent),
    [boardData],
  );

  // Filter *labels* come back in English whatever the user's language, so the
  // page supplies its own; the *options* are translated server-side and are
  // the only place the real congregations and gender values exist.
  const filterFields = useMemo(
    () =>
      listFiltersToFilterFields(filtersData?.filters, {
        intro: { label: t('wbs.intro-progress') },
        needs_encouragement: {
          label: t('wbs.needs-encouragement'),
          checkboxLabel: t('lessons.only-show-students-who-2'),
        },
        gender: { label: t('wbs.gender') },
        study_mode: { label: t('lessons.study-mode') },
        language: { label: t('wbs.language') },
        country: { label: t('lessons.student-country') },
        outreach_group_id: { label: t('wbs.outreach-target') },
        campaign_id: { label: t('wbs.matched-campaign') },
      }),
    [filtersData, t],
  );

  // Both actions are two-step, as in legacy: a confirm modal naming the student
  // and the outreach target consumed or released, then a success modal.
  const handleAdopt = (studentId: string): void => {
    setAdoptingStudentId(Number(studentId));
  };

  const handlePassOn = (studentId: string): void => {
    setPassingOnStudentId(Number(studentId));
  };

  const handleEncourage = (studentId: string): void => {
    setEncouragingStudentId(Number(studentId));
  };

  const handleAssign = (studentId: string): void => {
    setAssigningStudentId(Number(studentId));
  };

  // The encourage modal has no adoption-info read to take the name from, so it
  // comes from the clicked row.
  const encouragingStudentName =
    students.find((student) => Number(student.id) === encouragingStudentId)
      ?.name ?? '';

  const refreshBoard = (): void => {
    void api.getStudentBoard.invalidateQueries();
  };

  // `sortable` tracks the endpoint's declared sort keys; every column key below
  // happens to match one, so none of them need a `sortKey` override.
  const columns: Column<Student>[] = [
    {
      key: 'name',
      title: t('wbs.name'),
      sortable: true,
      width: '135px',
      render: (_value: string, row: Student) => (
        <NameCell>
          <AvatarImg src={row.avatarUrl} alt={row.name} />
          <span>{row.name}</span>
        </NameCell>
      ),
    },
    {
      key: 'age',
      title: t('wbs.age'),
      sortable: true,
      width: '52px',
      // Some students have no birth year. Legacy renders an empty cell; `--`
      // matches the Intro column beside it.
      render: (age: number | null) => age ?? '--',
    },
    {
      key: 'gender',
      title: t('wbs.gender'),
      sortable: true,
      width: '85px',
    },
    {
      key: 'language',
      title: t('wbs.language'),
      sortable: true,
      width: '120px',
      render: (language: LanguageCode) => languageLabels[language],
    },
    {
      key: 'city',
      title: t('wbs.location'),
      sortable: true,
      width: '250px',
      render: (_value: string, row: Student) => {
        const cityState = row.state ? `${row.city}, ${row.state}` : row.city;
        return (
          <LocationCell>
            <LocationLine>{cityState}</LocationLine>
            <LocationLine>{row.country}</LocationLine>
          </LocationCell>
        );
      },
    },
    {
      key: 'intro',
      title: t('wbs.intro'),
      sortable: true,
      width: '120px',
      render: (_value: Student['intro'], row: Student) => {
        let text = '--';
        if (row.intro === 'completed') {
          text = t('wbs.completed-3');
        } else if (
          row.intro === 'started' &&
          typeof row.introProgress === 'number'
        ) {
          text = `${t('wbs.started')} - ${row.introProgress}%`;
        }
        return <span style={{ fontSize: '13px' }}>{text}</span>;
      },
    },
    {
      key: 'registered',
      title: t('wbs.registered'),
      sortable: true,
      width: '210px',
      render: (value: string) => formatLongDate(value),
    },
    {
      key: 'mode',
      title: t('wbs.mode'),
      sortable: true,
      width: '65px',
      render: (value) => <ModeIconCell mode={value} />,
    },
    {
      key: 'actions',
      title: t('wbs.actions'),
      sortable: false,
      width: '250px',
      cellStyle: { textAlign: 'right' },
      render: (actions, row) => (
        <ActionsCell
          actions={actions}
          row={row}
          onAdopt={handleAdopt}
          onPassOn={handlePassOn}
          onEncourage={handleEncourage}
          onAssign={handleAssign}
        />
      ),
    },
  ];
  return (
    <PageContainer>
      <AdoptStudentModal
        studentId={adoptingStudentId}
        onClose={() => setAdoptingStudentId(null)}
        onAdopted={refreshBoard}
      />
      <PassOnStudentModal
        studentId={passingOnStudentId}
        onClose={() => setPassingOnStudentId(null)}
        onPassedOn={refreshBoard}
      />
      {/* Both modals are two-step popups on this table, so they mount
         beside adopt and pass-on rather than firing a bare mutation. */}
      <EncourageStudentModal
        studentId={encouragingStudentId}
        studentName={encouragingStudentName}
        onClose={() => setEncouragingStudentId(null)}
        onEncouraged={refreshBoard}
      />
      <AssignStudentModal
        studentId={assigningStudentId}
        onClose={() => setAssigningStudentId(null)}
        onAssigned={refreshBoard}
      />
      <div style={{ margin: '9px 0 29px 0' }}>
        <TitleWithQuestion
          title={t('lessons.student-board')}
          placement="student_board"
        />
      </div>
      {/* The app shell has its own "Search" box, so the table's controls need
          a scope an e2e test can anchor on. */}
      <div style={{ marginTop: '24px' }} data-testid="student-board-table">
        <WbsTable
          {...table.tableProps}
          data={students}
          columns={columns}
          totalCount={boardData?.count}
          loading={isFetching}
          onRefresh={() => {
            void refetch();
          }}
          showSearch
          showFilter
          showSettings
          showRefresh
          cellStyle={{ padding: '14px 8px' }}
          filterFields={filterFields}
        />
      </div>
    </PageContainer>
  );
}

const LocationCell = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 13px;
  gap: 2px;
`;

const LocationLine = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
